<template>
	<div v-if="product" class="flex flex-col h-full cursor-pointer gap-y-4">
		<NuxtLink
			:to="clickIsValid ? routeToProduct : ''"
			:class="linkStyles || ''"
			@click="handleClick"
		>
			<div
				class="flex flex-col w-full relative items-start"
				:class="contentStyles || ''"
			>
				<Favorite
					v-if="product.Id"
					:absolute="true"
					:wrapper-size="8"
					rounded="full"
					:product-id="product.Id"
					:product="product"
				/>
				<div
					:class="`relative bg-product-tile ${pictureSize} overflow-hidden aspect-[${pictureAspectRatio}]`"
				>
					<div class="flex items-center justify-center h-full w-full p-3">
						<!-- Norce images use a hand-built srcset (utils/product/norceImage.ts):
						NuxtImg's preload next to its own srcset can fetch a photo twice. -->
						<img
							v-if="cardImage"
							:src="cardImage.src"
							:srcset="cardImage.srcset"
							:sizes="CARD_IMAGE_SIZES"
							:alt="product?.Name || 'Default product image'"
							class="max-h-full max-w-[85%] w-full object-contain cursor-pointer mix-blend-multiply"
							:loading="lazyLoading ? 'lazy' : 'eager'"
							:fetchpriority="lazyLoading ? 'auto' : 'high'"
							width="364"
							height="546"
						/>
						<NuxtImg
							v-else
							:src="defaultImage"
							:alt="product?.Name || 'Default product image'"
							class="max-h-full max-w-[85%] w-full object-contain cursor-pointer mix-blend-multiply"
							:loading="lazyLoading ? 'lazy' : 'eager'"
							width="364"
							height="546"
							provider="storyblok"
						/>
					</div>
					<ApplicationFlag
						v-for="(flagId, index) in productFlags"
						:key="flagId"
						:data="getFlagData(flagId)"
						:position="getItemPosition(flagId, index)"
						class="px-2"
					/>
				</div>
				<div class="flex flex-col mt-4 gap-2 items-start w-full">
					<span
						class="font-medium text-base text-left"
						:class="showAddToCart && 'line-clamp-2 min-h-12 w-full'"
					>
						{{ product.Name }}
					</span>
					<ProductSpecialPriceLabel
						v-if="product.PriceListId && specialPrice"
						:price-list-id="product.PriceListId"
						:special-price="specialPrice"
						class="!relative !p-0 !text-red-light !bg-transparent !max-w-full"
					/>
					<ProductPromotionText
						:product="product"
						:special-price="specialPrice"
						class="!relative !p-0 !text-red-light !bg-transparent !max-w-full"
					/>
					<Price
						:price="product.PriceIncVat!"
						:old-price="oldPrice"
						:show-save-text="false"
						:show-previous="false"
					/>
					<ProductProductAttributesStockSituation :stock-status="stockStatus" />
				</div>
			</div>
		</NuxtLink>

		<CatalogAddToCart
			v-if="showAddToCart"
			:product="product"
			:disabled="isAddToCartDisabled"
			:stock-status="stockStatus"
			class="mt-auto w-full"
		/>
	</div>
</template>

<script setup lang="ts">
import { parametricsSeedToQuery } from "~/utils/parametricsUrl";
import { computed } from "vue";
import {
	CARD_IMAGE_SIZES,
	buildNorceCardImage
} from "~/utils/product/norceImage";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type {
	ParametricListValue,
	ProductItem
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import Price from "./ProductAttributes/Price.vue";
import type { SpecialPrice } from "~/types/storyblok/storyblok";

interface Props {
	product: Partial<FilteredProductItem> | ProductItem;
	pictureSize: string;
	pictureAspectRatio?: string;
	clickIsValid?: boolean;
	lazyLoading?: boolean | undefined;
	specialPrice?: SpecialPrice | null;
	/** Opt-in: carousels and the recommendation block pass it, listings do not. */
	showAddToCart?: boolean | undefined;
	allParametrics?: ParametricListValue[];
	contentStyles?: string;
	linkStyles?: string;
}
const {
	product,
	pictureAspectRatio,
	pictureSize,
	lazyLoading,
	clickIsValid = true,
	showAddToCart,
	allParametrics
} = defineProps<Props>();

const cardImage = computed(() => {
	const path = product?.Files?.[0]?.Path;
	return path ? buildNorceCardImage(path) : null;
});
const emit = defineEmits(["product-click"]);

const productStore = useProductStore();
const { productClick } = useGtmEvents();
const { oldPrice } = useProductPrices(product);
const { getStockStatus } = useStockStatus();
const { getProductStock } = useNorcePwa();

const isInStock = computed(() => product.inStock ?? !!getProductStock(product));
const stockStatus = computed(() =>
	getStockStatus(product?.FlagIdSeed, isInStock.value)
);

const defaultImage = computed(() => {
	return productStore.defaultProductImage || "images/product/no_image.jpeg";
});

const localePath = useLocalePath();

const routeToProduct = computed(() => {
	const path = localePath(`/${product?.UniqueName}`);
	if (!product?.ParametricListSeed || !product?.VariantParametricSeed?.length) {
		return path;
	}
	if (allParametrics?.length) {
		const query = parametricsSeedToQuery(
			product.ParametricListSeed,
			allParametrics
		);
		return Object.keys(query).length ? { path, query } : path;
	}
	return path;
});
const {
	flagIds: productFlags,
	getItemPosition,
	getFlagData
} = useProductFlags(() => product, "plp");

const isAddToCartDisabled = computed(
	() =>
		!product.PartNo ||
		!product.PriceListId ||
		(stockStatus.value?.disableAddToCart && !isInStock.value)
);

const handleClick = () => {
	if (clickIsValid) {
		productClick(product);
		emit("product-click");
	}
};
</script>
